import React, { useState, useEffect, useCallback } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { ApiURLs, imageBaseUrls } from '../../constant/ApiUrls';
import { fetchData } from '../../services/api';

/*
  Every message ever sent, readable here.

  This reads `message_archive`, not the live `messages` table. Until this week a
  foreign key with ON DELETE CASCADE meant that one person pressing "Remove" on
  their own Messages screen destroyed the whole thread out of the live table,
  for both sides, permanently. 126 threads went that way before it was found.
  The archive has no foreign key onto anything and nothing in the code deletes
  from it, so it holds the live threads and the destroyed ones side by side.

  Each row says which it is, and the transcript marks the individual messages
  that are no longer anywhere else - for those, this screen is the only copy.

  The two sites are two databases on two machines and conversation numbers
  collide across them, so they are never merged into one list: you look at one
  site at a time and the heading says which one answered.
*/

const SITE_OPTIONS = [
    { key: 'self', label: 'This site' },
    { key: 'peer', label: 'Other site' },
];

const PAGE_SIZES = [10, 25, 50];

const when = (v) => {
    if (!v) return '';
    const d = new Date(v);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
};

const Pill = ({ children, background, colour, border }) => (
    <span style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11,
        fontWeight: 700, background, color: colour, border: border || 'none',
        whiteSpace: 'nowrap',
    }}>{children}</span>
);

const MessageArchive = () => {
    const [site, setSite] = useState('self');
    const [siteLabel, setSiteLabel] = useState('');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [search, setSearch] = useState('');
    // Kept apart from `search` so the list is not refetched on every keystroke.
    const [query, setQuery] = useState('');
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [notice, setNotice] = useState('');

    const [openId, setOpenId] = useState(null);
    const [thread, setThread] = useState(null);
    const [threadLoading, setThreadLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setNotice('');
        try {
            const url = `${ApiURLs.MESSAGE_ARCHIVE}?site=${site}&page=${page + 1}`
                + `&limit=${pageSize}&q=${encodeURIComponent(query)}`;
            const res = await fetchData(url);
            if (res && res.status === 1) {
                setRows(res.rows || []);
                setTotal(res.total || 0);
                setSiteLabel(res.site || '');
            } else {
                // Deliberately not showing an empty table as if it were an
                // answer: "no conversations" and "could not ask" look identical
                // and only one of them is news.
                setRows([]); setTotal(0); setSiteLabel('');
                setNotice((res && res.message) || 'Could not load the messages.');
            }
        } catch (e) {
            setRows([]); setTotal(0);
            setNotice('Could not load the messages.');
        }
        setLoading(false);
    }, [site, page, pageSize, query]);

    useEffect(() => { load(); }, [load]);

    const openThread = async (id) => {
        setOpenId(id);
        setThread(null);
        setThreadLoading(true);
        try {
            const res = await fetchData(`${ApiURLs.MESSAGE_ARCHIVE}/${id}?site=${site}`);
            setThread(res && res.status === 1 ? res.thread : null);
        } catch (e) {
            setThread(null);
        }
        setThreadLoading(false);
    };

    const columns = [
        {
            field: 'last_at', headerName: 'Last message', width: 150,
            renderCell: (p) => <span style={{ fontSize: 13 }}>{when(p.value)}</span>,
        },
        {
            field: 'ad_title', headerName: 'Advert', flex: 1.2, minWidth: 170,
            renderCell: (p) => (
                <span title={p.value} style={{ fontSize: 13 }}>
                    {p.value || <em style={{ color: '#999' }}>no advert</em>}
                </span>
            ),
        },
        {
            field: 'participants', headerName: 'Between', flex: 1, minWidth: 170, sortable: false,
            renderCell: (p) => (
                <span style={{ fontSize: 13 }}>
                    {(p.value || []).map((u) => u.name).join('  and  ') || '—'}
                </span>
            ),
        },
        {
            field: 'message_count', headerName: 'Messages', width: 90, align: 'center', headerAlign: 'center',
            renderCell: (p) => <span style={{ fontWeight: 600 }}>{p.value}</span>,
        },
        {
            field: 'status', headerName: 'Status', width: 165, sortable: false,
            renderCell: (p) => {
                const r = p.row;
                const lost = r.message_count - r.live_message_count;
                return (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', height: '100%' }}>
                        {!r.thread_live || lost > 0
                            ? <Pill background="#fde8e8" colour="#a51b1b">Only copy left</Pill>
                            : <Pill background="#e7f7ee" colour="#12704a">Live</Pill>}
                        {r.hidden_from && r.hidden_from.length ? (
                            <Pill background="#fff5e0" colour="#8a6100" border="1px solid #f2d79b">
                                {`Hidden by ${r.hidden_from.join(', ')}`}
                            </Pill>
                        ) : null}
                    </div>
                );
            },
        },
        {
            field: 'action', headerName: '', width: 80, sortable: false, filterable: false,
            renderCell: (p) => (
                <button
                    onClick={() => openThread(p.row.conversation_id)}
                    style={{
                        background: '#1b87f4', color: '#fff', border: 'none', cursor: 'pointer',
                        borderRadius: 4, fontSize: 12, padding: '5px 12px', fontWeight: 600,
                    }}
                >Read</button>
            ),
        },
    ];

    // Two participants, two colours, so a long transcript is readable at a
    // glance without having to re-read the name on every line.
    const colourFor = (thr, senderId) => {
        const first = thr && thr.messages.length ? thr.messages[0].sender.id : null;
        return senderId === first ? '#1b87f4' : '#7a3fd1';
    };

    return (
        <div style={{ padding: 24, background: '#f7f8fa', minHeight: '100vh' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                <h2 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: '#1a1a2e' }}>Messages</h2>
                {siteLabel ? <span style={{ fontSize: 14, color: '#666' }}>{siteLabel}</span> : null}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {SITE_OPTIONS.map((o) => (
                        <button key={o.key}
                            onClick={() => { setSite(o.key); setPage(0); }}
                            style={{
                                padding: '7px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13,
                                border: site === o.key ? '1px solid #0088FE' : '1px solid #d5d9e0',
                                background: site === o.key ? '#0088FE' : '#fff',
                                color: site === o.key ? '#fff' : '#333',
                            }}>{o.label}</button>
                    ))}
                </div>
            </div>

            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#666', maxWidth: 900 }}>
                Every message sent on the site, kept in a separate archive that nothing can delete.
                Threads marked <strong>Only copy left</strong> are ones somebody removed from their
                inbox before this archive existed &mdash; what you see here is all that survives of them.
            </p>

            <form
                onSubmit={(e) => { e.preventDefault(); setPage(0); setQuery(search.trim()); }}
                style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}
            >
                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search the words, a name, an email address or an advert title"
                    style={{
                        flex: '1 1 340px', maxWidth: 520, padding: '9px 12px', borderRadius: 6,
                        border: '1px solid #d5d9e0', fontSize: 14,
                    }}
                />
                <button type="submit" style={{
                    padding: '9px 18px', borderRadius: 6, border: 'none', background: '#1b87f4',
                    color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                }}>Search</button>
                {query ? (
                    <button type="button"
                        onClick={() => { setSearch(''); setQuery(''); setPage(0); }}
                        style={{
                            padding: '9px 16px', borderRadius: 6, border: '1px solid #d5d9e0',
                            background: '#fff', color: '#333', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                        }}>Clear</button>
                ) : null}
            </form>

            {notice ? (
                <div style={{
                    background: '#fef3c7', border: '1px solid #f6d174', color: '#8a6100',
                    borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 14,
                }}>{notice}</div>
            ) : null}

            {/* A fixed height rather than autoHeight: the grid is paginated on
                the server, so the page must not resize under the reader every
                time a page of a different length arrives. */}
            <div style={{
                background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                border: '1px solid #f0f0f0', height: 660,
            }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowId={(r) => r.conversation_id}
                    loading={loading}
                    rowCount={total}
                    rowHeight={58}
                    paginationMode="server"
                    paginationModel={{ page, pageSize }}
                    onPaginationModelChange={({ page: p, pageSize: s }) => { setPage(p); setPageSize(s); }}
                    pageSizeOptions={PAGE_SIZES}
                    disableRowSelectionOnClick
                    sx={{ border: 0 }}
                />
            </div>

            <Dialog open={!!openId} onClose={() => { setOpenId(null); setThread(null); }} maxWidth="md" fullWidth>
                <DialogTitle style={{ paddingBottom: 6 }}>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>
                        {thread && thread.ad ? thread.ad.title : 'Conversation'}
                    </div>
                    {thread ? (
                        <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                            Conversation {thread.conversation_id}
                            {thread.buyer && thread.seller
                                ? ` · ${thread.buyer.name} and ${thread.seller.name}` : ''}
                            {' · '}{thread.messages.length} message{thread.messages.length === 1 ? '' : 's'}
                        </div>
                    ) : null}
                </DialogTitle>
                <DialogContent dividers style={{ background: '#f7f8fa' }}>
                    {threadLoading ? (
                        <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading…</div>
                    ) : !thread ? (
                        <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
                            Nothing held for that conversation.
                        </div>
                    ) : (
                        <>
                            {!thread.thread_live ? (
                                <div style={{
                                    background: '#fde8e8', border: '1px solid #f3bcbc', color: '#a51b1b',
                                    borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13,
                                }}>
                                    This conversation was deleted from the site. Everything below was
                                    recovered and is the only copy that exists.
                                </div>
                            ) : thread.hidden_from && thread.hidden_from.length ? (
                                <div style={{
                                    background: '#fff5e0', border: '1px solid #f2d79b', color: '#8a6100',
                                    borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13,
                                }}>
                                    Removed from {thread.hidden_from.map((u) => u.name).join(' and ')}
                                    {thread.hidden_from.length === 1 ? "'s" : "'"} inbox. The other side still
                                    has it, and a reply brings it back for both.
                                </div>
                            ) : null}

                            {thread.messages.map((m, i) => (
                                <div key={m.message_id || i} style={{
                                    background: '#fff', borderRadius: 8, padding: '10px 14px', marginBottom: 10,
                                    borderLeft: `4px solid ${colourFor(thread, m.sender.id)}`,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                }}>
                                    <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                                        <strong style={{ fontSize: 13, color: colourFor(thread, m.sender.id) }}>
                                            {m.sender.name}
                                        </strong>
                                        <span style={{ fontSize: 12, color: '#999' }}>{when(m.sent_at)}</span>
                                        {m.recovered ? (
                                            <Pill background="#fde8e8" colour="#a51b1b">recovered</Pill>
                                        ) : null}
                                    </div>
                                    {m.message_text ? (
                                        <div style={{ fontSize: 14, marginTop: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            {m.message_text}
                                        </div>
                                    ) : null}
                                    {m.image_url ? (
                                        <img
                                            src={imageBaseUrls(m.image_url)}
                                            alt=""
                                            style={{ marginTop: 8, maxWidth: 240, borderRadius: 6, display: 'block' }}
                                        />
                                    ) : null}
                                </div>
                            ))}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setOpenId(null); setThread(null); }}>Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default MessageArchive;
